import localforage from "localforage";
import { logger } from "~/lib/logger";

// AIDEV-NOTE: Deprecated — kept read-only for recovering old recordings stored before the durable recorder migration. New recordings use ~/hooks/media/durable-audio-recorder-db.ts

// AIDEV-NOTE: LocalForage-based storage for audio recordings with auto-cleanup of week-old data

export interface LegacyRecordingData {
    recordDataBase64: string;
    mimeType: string;
    msDuration: number;
    createdAt: number; // timestamp
    sessionId?: string; // if upload was initiated
    practitionerId?: string | null; // for retry uploads
    clientId?: string | null; // for retry uploads
}

// Configure localForage instance for recordings
const recordingStore = localforage.createInstance({
    name: "IFSCompanion",
    storeName: "recordings",
    driver: [
        localforage.INDEXEDDB,
        localforage.WEBSQL,
        localforage.LOCALSTORAGE,
    ],
    description: "Storage for audio recordings before upload",
});

// AIDEV-NOTE: Legacy-store retention. Raised 7 -> 30 days (Jul 2026): the
// durable store has no TTL, and a 7-day cap silently destroyed recordings
// users could otherwise still rescue via the recovery banner.
const LEGACY_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Save a recording to IndexedDB
 * @deprecated Use ~/hooks/media/durable-audio-recorder-db.ts for new recordings.
 */
export async function saveRecording(data: {
    recordDataBase64: string;
    mimeType: string;
    msDuration: number;
    sessionId?: string;
    practitionerId?: string | null;
    clientId?: string | null;
}): Promise<string> {
    const key = `recording_${Date.now()}`;
    const recordingData: LegacyRecordingData = {
        ...data,
        createdAt: Date.now(),
    };

    try {
        await recordingStore.setItem(key, recordingData);
        logger.withMetadata({ key }).info("Recording saved");
        // Clean up old recordings after saving
        await cleanupOldRecordings();
        return key;
    } catch (error) {
        console.error("Failed to save recording:", error);
        throw new Error(
            `Failed to save recording: ${
                error instanceof Error ? error.message : "Unknown error"
            }`,
        );
    }
}

/**
 * Get a recording from IndexedDB
 */
export async function getRecording(
    key: string,
): Promise<LegacyRecordingData | null> {
    try {
        const data = await recordingStore.getItem<LegacyRecordingData>(key);
        return data;
    } catch (error) {
        console.error(`Failed to get recording ${key}:`, error);
        return null;
    }
}

/**
 * Delete a recording from IndexedDB
 */
export async function deleteRecording(key: string): Promise<void> {
    try {
        await recordingStore.removeItem(key);
        logger.withMetadata({ key }).info("Recording deleted");
    } catch (error) {
        console.error(`Failed to delete recording ${key}:`, error);
    }
}

/**
 * Get all stored recordings
 */
export async function getAllRecordings(): Promise<
    Array<{ key: string; data: LegacyRecordingData }>
> {
    const recordings: Array<{ key: string; data: LegacyRecordingData }> = [];

    try {
        const keys = await recordingStore.keys();
        for (const key of keys) {
            const data = await recordingStore.getItem<LegacyRecordingData>(key);
            if (data) {
                recordings.push({ key, data });
            }
        }
    } catch (error) {
        console.error("Failed to get all recordings:", error);
    }

    return recordings;
}

/**
 * Clean up legacy recordings older than the retention window (30 days)
 */
export async function cleanupOldRecordings(): Promise<number> {
    let deletedCount = 0;
    const now = Date.now();

    try {
        const recordings = await getAllRecordings();

        for (const { key, data } of recordings) {
            const age = now - data.createdAt;
            if (age > LEGACY_RETENTION_MS) {
                await deleteRecording(key);
                deletedCount++;
            }
        }

        if (deletedCount > 0) {
            logger
                .withMetadata({ deletedCount })
                .info("Cleaned up old recordings");
        }
    } catch (error) {
        console.error("Failed to cleanup old recordings:", error);
    }

    return deletedCount;
}

/**
 * Get the total size of all stored recordings (approximate)
 */
export async function getTotalStorageSize(): Promise<number> {
    let totalSize = 0;

    try {
        const recordings = await getAllRecordings();
        for (const { data } of recordings) {
            // Approximate size: base64 is roughly 1.33x the original size
            totalSize += (data.recordDataBase64.length * 3) / 4;
        }
    } catch (error) {
        console.error("Failed to calculate storage size:", error);
    }

    return totalSize;
}

/**
 * Clear all recordings (use with caution)
 */
export async function clearAllRecordings(): Promise<void> {
    try {
        await recordingStore.clear();
        logger.info("All recordings cleared");
    } catch (error) {
        console.error("Failed to clear recordings:", error);
    }
}
